import { useQuery } from "@tanstack/react-query";
import { apiRequestRaw } from "../../../shared/api/http";
import { HttpError } from "../../../shared/api/http/httpError";
import type { RawCampaignCardResponse } from "../../../shared/types/campaign-card.type";
import {
	isValidCampaignCardId,
	normalizeCampaignCardResponse,
} from "../../../shared/utils/normalize-campaign-card";
import type { CampaignCardView } from "../../../shared/types/campaign-card.type";
import { CampaignCardFetchError } from "../errors/campaign-card-fetch-error";

async function fetchCampaignCard(campaignId: string): Promise<CampaignCardView> {
	try {
		const data = await apiRequestRaw<RawCampaignCardResponse>({
			path: "/api/ui/campaign/card",
			method: "POST",
			json: {
				campaign_ids: [campaignId],
				limit: 100,
				offset: 0,
			},
		});
		const campaign = normalizeCampaignCardResponse(data, campaignId);
		if (!campaign) {
			throw new CampaignCardFetchError("not-found", 404);
		}
		return campaign;
	} catch (e) {
		if (e instanceof HttpError) {
			throw new CampaignCardFetchError("fetch-failed", e.status);
		}
		throw e;
	}
}

export const campaignCardQueryKey = (campaignId: string) =>
	["campaign-card", campaignId] as const;

export function useCampaignCard(campaignId: string | undefined) {
	const validId =
		campaignId && isValidCampaignCardId(campaignId) ? campaignId : undefined;

	return useQuery({
		queryKey: campaignCardQueryKey(validId ?? ""),
		queryFn: () => fetchCampaignCard(validId!),
		enabled: Boolean(validId),
		staleTime: 60_000,
		retry: false,
	});
}
